import { useSnoozeTask } from '@task-manager/entities/tasks';
import { DURATION_HINT } from '@task-manager/features/task-form/model/schema';
import { Clock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toastMutation } from '@/shared/lib';
import {
  Button,
  DateTimePicker,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  Text,
} from '@/shared/ui';
import {
  computeSnoozedUntilFromDuration,
  isValidDuration,
  normalizeDuration,
} from '../lib/duration';
import { computeSnoozedUntil, SNOOZE_PRESETS } from '../lib/snooze-presets';
import css from './task-snooze-modal.module.scss';

type TaskSnoozeModalProps = {
  open: boolean;
  taskId?: string;
  /** Current due date; snooze persists through the legacy expireOn field. */
  currentSnoozedUntil?: string | null;
  onClose: () => void;
};

const formatSnoozedUntil = (iso: string | null | undefined): string => {
  if (!iso) return 'No date set';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'No date set';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
};

const isFutureIso = (iso: string | undefined): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getTime() > Date.now();
};

export const TaskSnoozeModal = ({
  open,
  taskId,
  currentSnoozedUntil,
  onClose,
}: TaskSnoozeModalProps) => {
  const mutation = useSnoozeTask();
  const [customValue, setCustomValue] = useState<string | undefined>(
    currentSnoozedUntil ?? undefined
  );
  const [durationValue, setDurationValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const currentLabel = useMemo(
    () => formatSnoozedUntil(currentSnoozedUntil),
    [currentSnoozedUntil]
  );

  // Live-normalized preview of the typed duration (e.g. 26h → "1d 2h").
  // null while the field is empty or the format is invalid.
  const durationPreview = useMemo(() => {
    if (durationValue.trim().length === 0) return null;
    return normalizeDuration(durationValue);
  }, [durationValue]);

  const isDurationInvalid =
    durationValue.trim().length > 0 && !isValidDuration(durationValue);

  useEffect(() => {
    if (!open) return;
    setCustomValue(currentSnoozedUntil ?? undefined);
    setDurationValue('');
    setError(null);
  }, [open, currentSnoozedUntil]);

  const save = (snoozedUntil: string | null) => {
    if (!taskId) return;
    setError(null);
    mutation.mutate(
      { id: taskId, snoozedUntil },
      toastMutation({ success: 'Task snoozed' }, { onSuccess: onClose })
    );
  };

  const saveCustom = () => {
    // A typed custom-duration takes precedence over the date picker.
    if (durationValue.trim().length > 0) {
      if (!isValidDuration(durationValue)) {
        setError(`Invalid duration format. ${DURATION_HINT}`);
        return;
      }
      const snoozedUntil = computeSnoozedUntilFromDuration(durationValue);
      if (!snoozedUntil) {
        setError(`Invalid duration format. ${DURATION_HINT}`);
        return;
      }
      save(snoozedUntil);
      return;
    }

    if (!isFutureIso(customValue)) {
      setError('Pick a future date and time');
      return;
    }
    save(customValue ?? null);
  };

  return (
    <Modal className={css.modal} open={open} onClose={onClose}>
      <ModalHeader
        className={css.header}
        onClose={onClose}
        title="Snooze task"
      />
      <div className={css.body}>
        <div className={css.current}>
          <span className={css.currentIcon}>
            <Clock size={18} />
          </span>
          <div className={css.currentText}>
            <Text as="span" size="xs" variant="muted" weight="medium">
              Current snooze
            </Text>
            <Text as="span" size="sm" weight="semibold">
              {currentLabel}
            </Text>
          </div>
        </div>

        <div className={css.presets}>
          {SNOOZE_PRESETS.map((preset) => (
            <Button
              className={css.presetButton}
              disabled={mutation.isPending}
              key={preset.label}
              onClick={() => save(computeSnoozedUntil(preset.hours))}
              size="lg"
              type="button"
              variant="outline"
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <div className={css.custom}>
          <Text as="span" size="sm" weight="medium">
            Custom duration
          </Text>
          <Input
            aria-invalid={isDurationInvalid}
            className={css.durationInput}
            disabled={mutation.isPending}
            leadingIcon={<Clock size={16} />}
            onChange={(e) => {
              setDurationValue(e.target.value);
              setError(null);
            }}
            placeholder="e.g. 1d 5h, 3h 30m, 2w 3d"
            value={durationValue}
          />
          {isDurationInvalid ? (
            <Text as="p" size="sm" variant="error">
              {`Invalid duration format. ${DURATION_HINT}`}
            </Text>
          ) : durationPreview ? (
            <Text as="p" size="sm" variant="muted">
              {`Snooze for ${durationPreview}`}
            </Text>
          ) : (
            <Text as="p" size="xs" variant="muted">
              {DURATION_HINT}
            </Text>
          )}

          <Text as="span" size="sm" weight="medium">
            Or pick a custom date
          </Text>
          <DateTimePicker
            className={css.dateTimePicker}
            disabled={mutation.isPending || durationValue.trim().length > 0}
            onChange={setCustomValue}
            value={customValue}
          />
          {error ? (
            <Text as="p" size="sm" variant="error">
              {error}
            </Text>
          ) : null}
        </div>
      </div>
      <ModalFooter className={css.footer}>
        <Button
          disabled={mutation.isPending}
          onClick={onClose}
          size="lg"
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          disabled={mutation.isPending}
          onClick={saveCustom}
          size="lg"
          type="button"
          variant="primary"
        >
          {mutation.isPending ? 'Saving...' : 'Save custom'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
